import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Buildings, GearSix, SignOut, ShieldStar } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import SkipToMain from "@/components/a11y/SkipToMain";

const PLATFORM_NAV = [
  { to: "/platform", label: "Centers", icon: Buildings, end: true },
];

export default function PlatformShell() {
  const { profile, platformRole, signOut } = useAuth();
  const navigate = useNavigate();
  const isOwner = platformRole === "platform_owner";

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] text-neutral-900" data-testid="platform-shell">
      <SkipToMain targetId="main" />
      <aside
        className="fixed inset-y-0 left-0 z-40 hidden w-72 flex-col border-r border-neutral-200 bg-white px-5 py-7 md:flex"
        aria-label="Platform navigation"
      >
        <div className="rounded-3xl bg-neutral-900 p-4 text-white shadow-card">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#D7E8A9] text-neutral-900">
              <ShieldStar size={24} weight="duotone" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/65">HoneyAcorn</p>
              <p className="font-heading text-lg font-semibold tracking-tight">Platform control</p>
            </div>
          </div>
          <p className="mt-4 text-xs leading-5 text-white/70">
            Center setup and administrator access. Family and child records are intentionally out of scope here.
          </p>
        </div>

        <nav className="mt-7 flex-1" aria-label="Platform">
          <ul className="space-y-1">
            {PLATFORM_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-2xl px-3 py-3 text-sm font-semibold transition ${
                        isActive
                          ? "bg-primary/10 text-primary-hover"
                          : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                      }`
                    }
                  >
                    {({ isActive }) => <Icon size={20} weight={isActive ? "fill" : "duotone"} aria-hidden />}
                    {item.label}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="border-t border-neutral-200 pt-4">
          <div className="mb-3 flex items-center gap-3 px-1">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-neutral-100 text-sm font-semibold text-neutral-700">
              {(profile?.full_name || profile?.email || "?").slice(0, 1).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900">{profile?.full_name || "—"}</p>
              <p className="truncate text-xs text-neutral-500">{isOwner ? "Platform owner" : "Platform admin"}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-800 transition hover:bg-neutral-200"
          >
            <SignOut size={16} weight="duotone" />
            Sign out
          </button>
        </div>
      </aside>

      <header className="pt-safe-header sticky top-0 z-30 flex items-center justify-between border-b border-neutral-200 bg-white/95 px-4 pb-3 backdrop-blur md:hidden">
        <div className="flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-neutral-900 text-[#D7E8A9]">
            <ShieldStar size={19} weight="duotone" aria-hidden />
          </span>
          <div>
            <p className="font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-500">HoneyAcorn</p>
            <p className="font-heading text-sm font-semibold text-neutral-900">Platform control</p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSignOut}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-200 text-neutral-600"
          aria-label="Sign out"
        >
          <SignOut size={17} weight="duotone" />
        </button>
      </header>

      <main id="main" role="main" tabIndex={-1} className="min-h-screen md:pl-72">
        <div className="mx-auto w-full max-w-7xl px-5 py-7 md:px-12 md:py-12">
          <ErrorBoundary scope="platform">
            <Outlet />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}
